"use client";

import React, { useEffect, useRef, useState } from "react";
import { Clock, ShieldCheck, BadgeDollarSign, MapPin } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const benefits = [{
  title: "Proprietary Credit Scaling",
  description: "Leverage our exclusive techniques to rapidly build personal and business credit profiles. We navigate complex financial systems to secure the high-limit funding your enterprise deserves.",
  icon: BadgeDollarSign,
  className: "md:col-span-2 lg:col-span-2 lg:row-span-2 flex flex-col justify-center",
  isLarge: true
}, {
  title: "1-on-1 Executive Consulting",
  description: "Direct access to senior financial strategists who specialize in capital management and business scaling. We provide the roadmap for your transition from stability to high-performance growth.",
  icon: ShieldCheck,
  className: "md:col-span-2 lg:col-span-2 lg:row-span-1",
  isLarge: false
}, {
  title: "Rapid Funding Access",
  description: "Get the capital you need without the wait. Our network connects you to premium funding sources and metal-tier credit opportunities.",
  icon: Clock,
  className: "md:col-span-1 lg:col-span-1 lg:row-span-1",
  isLarge: false
}, {
  title: "Global Business Management",
  description: "Beyond credit, we manage your financial reputation. Our expertise ensures your business remains attractive to top-tier lenders and partners.",
  icon: MapPin,
  className: "md:col-span-1 lg:col-span-1 lg:row-span-1",
  isLarge: false
}];
export default function WhyChooseSmithServices() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="223022" id="wealth-scaling-strategies" className="relative w-full overflow-hidden bg-background text-foreground py-24 lg:py-32">
      {}
      <div className="pointer-events-none absolute top-0 left-0 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] rounded-lg bg-primary/20 blur-[100px]" />
      <div className="pointer-events-none absolute bottom-0 right-0 translate-x-1/3 translate-y-1/3 h-[600px] w-[600px] rounded-lg bg-chart-4/10 blur-[120px]" />

      <div className="container mx-auto max-w-7xl px-6 relative z-10">
        <div className="mb-16 max-w-3xl">
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl mb-4 uppercase">
            Wealth Scaling Strategies
          </h2>
          <p className="text-lg text-muted-foreground font-default">
            High-Performance Financial Luxury. Lake Stone Enterprise blends traditional stability with modern fintech velocity to scale your personal and business credit profiles.
          </p>
        </div>

        <div ref={ref} className={cn("grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          {benefits.map((item, i) => {
          const Icon = item.icon;
          return <Card key={i} data-index={i} className={cn("bg-card/60 backdrop-blur-xl border-t-primary/50 border-x-border/20 border-b-border/20 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1", item.className)}>
                <CardHeader className={item.isLarge ? "pb-4 md:pb-6" : "pb-2"}>
                  <div className="mb-4">
                    {item.isLarge ? <div className="relative inline-flex">
                        <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-primary/20">
                          <Icon className="size-8 text-primary" strokeWidth={1.5} />
                        </div>
                        <span className="absolute -top-1 -right-1 flex size-4">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-lg bg-primary opacity-75"></span>
                          <span className="relative inline-flex rounded-lg size-4 bg-primary border-2 border-background"></span>
                        </span>
                      </div> : <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-secondary">
                        <Icon className="size-6 text-primary" strokeWidth={1.5} />
                      </div>}
                  </div>
                  <CardTitle className={cn("font-default font-bold text-foreground", item.isLarge ? "text-2xl md:text-3xl lg:text-4xl" : "text-xl")}>
                    {item.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className={cn("font-default text-muted-foreground", item.isLarge ? "text-base md:text-lg leading-relaxed" : "text-sm leading-relaxed")}>
                    {item.description}
                  </CardDescription>
                </CardContent>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}